import CommonPopup from "@theme/component-common-popup";
import ShokzMember from "@theme/shokz-member";
import { ref, createApp, onBeforeMount, computed, onBeforeUnmount } from "vue";

/**
 * @import {SimpleToast} from '@theme/component-simple-toast'
 */

/** @type {SimpleToast} */
const simpleToast = document.getElementById('simple-toast');

/**
 * @import {Ref} from 'vue'
 * 
 * @typedef {import('@theme/shokz-member-type').EarningHistoryItem} EarningHistoryItem
 */

/**
 * @param {HubEarningHistory} section 
 * @returns 
 */
function createHubBenefitsVueEntry(section) {
  const app = createApp({
    delimiters: ['${', '}'],
    setup() {
      // ============ 响应式数据 ============
      const config = section.data;
      /**
       * @type {Ref<EarningHistoryItem[], EarningHistoryItem[]>}
       */
      const activities = ref(null);
      const historyItems = computed(() => activities.value?.filter(item => item?.points > 0) ?? []);
      const loading = ref(true);
      // 映射 ShokzMember.memberData.tier
      const currentTier = ref(0);
      const computedBadgeStatus = (_item) => {
        /**
         * @type {EarningHistoryItem}
         */
        const item = _item;
        const startAt = item.timestamp != undefined ? new Date(item.timestamp).getTime() : undefined;
        // 过期时间，未指定时，配置为发放时间 + 12个月
        const endsAt = item.expdate != undefined
          ? new Date(item.expdate).getTime()
          : (item.timestamp != undefined ? new Date(item.timestamp).getTime() + 31104000000 /* 12个月 */ : undefined);
        const current = new Date().getTime();
        // 未生效
        if (startAt != undefined && current < startAt) {
          return 'pending';
        }
        // 过期
        if (endsAt != undefined && current > endsAt) {
          return 'voided';
        }
        // 生效中
        if (startAt != undefined && endsAt != undefined && current >= startAt && current < endsAt) {
          return 'active';
        }
        return 'active';
      };
      const computedBadgeClass = computed(() => (_item) => {
        const status = computedBadgeStatus(_item);
        return `badge--${status}`;
      });
      const computedBadgeLabel = computed(() => (_item) => {
        const status = computedBadgeStatus(_item);
        return config[`${status}_status_label`];
      });

      // ============ 生命周期 ============
      onBeforeMount(() => {
        reload();
      });
      onBeforeUnmount(() => {

      });

      // ============ 方法 ============
      const formatDate = (dateString) => {
        const date = new Date(dateString);
        const month = date.getMonth() + 1;
        const day = date.getDate();
        const year = date.getFullYear();
        // 格式化为 月/日/年
        return `${month}/${day}/${year}`;
      }

      const reload = () => {
        loading.value = true;
        fetchData().catch(error => {
          simpleToast.show(error?.message || 'An unknown error occurred, please try again later.');
          console.error('[section-hub-earning-history]: fetchData error', error);
        }).finally(() => {
          loading.value = false;
        });
      }

      const fetchData = async () => {
        const result = await ShokzMember.getPointsHistory();
        activities.value = result.data.data;
      }

      const onRetry = () => {
        loading.value = true;
        reload();
      }

      // ============ 返回值 ============
      // 返回所有需要在模板中使用的响应式数据和方法
      return {
        config,
        // 响应式数据
        activities,
        historyItems,
        loading,
        computedBadgeStatus,
        computedBadgeLabel,
        computedBadgeClass,
        currentTier,
        // 方法
        formatDate,
        onRetry,
      };
    },
    compilerOptions: {
      // 标记哪些标签是 WebComponents，避免和 Vue 冲突
      isCustomElement: (tag) => ['hub-earning-history'].includes(tag),
    }
  });

  app.mount(section.querySelector('[data-vue-render]'));
  return app;
}

class HubEarningHistory extends CommonPopup {
  /** @type { {pending_status_label: string, active_status_label: string, voided_status_label: string } } */
  data = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    super.connectedCallback();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
  }

  open() {
    if (this.data == undefined) {
      const scriptElement = this.querySelector('[data-json]');
      this.data = JSON.parse(scriptElement.innerHTML);
    }
    if (this.vueEntry == undefined) {
      this.vueEntry = createHubBenefitsVueEntry(this);
    }
    super.open();
  }

  close() {
    super.close();
  }
}

if (!customElements.get('hub-earning-history')) {
  customElements.define('hub-earning-history', HubEarningHistory);
} 